import React, { useState, useMemo, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { getListId } from '@alltrails/shared/utils/lists/listHelpers';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import { GeoLocationProvider } from 'components/explore/GeoLocationProvider';
import SearchInsightsProvider from 'components/SearchInsightsProvider';
import ErrorFallback from 'components/shared/ErrorFallback';
import { Page, PageSource } from 'components/shared/Page/Page';
import useResizeListener from 'hooks/useResizeListener';
import { Context } from '@alltrails/shared/types/context';
import logError from 'utils/logError';
import useListItems from '../../hooks/useListItems';
import NewFullscreenSearchApp from './NewFullscreenSearchApp';

type Props = {
  context: Context;
  initialCustomList: any;
  initialMyLists: any;
  initialMyListItems: any;
  initialPage: string;
  initialUserListItems: any;
  userInfo: any;
  locationData: {
    city: {
      names: { [key: string]: any[] };
    };
    country: string;
    latLng: (string | number)[];
  };
};

const listAccessForAdminOrCurrentUser = ({ context, userInfo }: Props, page: string) => {
  if (context.adminUser && page === PageStrings.EXPLORE_COMPLETED_PAGE) {
    return true;
  }

  return userInfo && context && context.currentUser && context.currentUser.id == userInfo.id;
};

const SearchApp = (props: Props) => {
  const [page, setPage] = useState(props.initialPage || PageStrings.EXPLORE_ALL_PAGE);
  const [isMobileWidth, setIsMobileWidth] = useState(props.context.mobileBrowser || false);
  const listId = useMemo(() => getListId(page, props.initialCustomList), [page, props.initialCustomList]);
  const belongsToCurrentUser = listAccessForAdminOrCurrentUser(props, page);
  const { listMethods, renderModal, listItems, lists, latestListAction, userListItems } = useListItems(
    {
      lists: props.initialMyLists,
      listItems: props.initialMyListItems,
      userListItems: props.initialUserListItems,
      belongsToCurrentUser: belongsToCurrentUser && listId // this decides whether or not to edit an individual users list
    },
    props.context,
    page
  );

  const updatePage = (newPage: string) => {
    setPage(newPage);
  };

  const retryErrorPage = () => {
    window.location.reload();
  };

  const onResize = useCallback(() => {
    setIsMobileWidth(window.innerWidth <= 767);
  }, []);

  useResizeListener({ onResize });

  return (
    <Page pageSource={PageSource.Explore} context={props.context}>
      <GeoLocationProvider>
        <SearchInsightsProvider context={props.context} pageName={PageSource.Explore}>
          {renderModal()}
          <ErrorBoundary fallback={<ErrorFallback retryErrorPage={retryErrorPage} />} onError={logError}>
            <NewFullscreenSearchApp
              {...props}
              isMobileWidth={isMobileWidth}
              latestListAction={latestListAction}
              lists={lists}
              listItems={listItems}
              page={page}
              userListItems={userListItems}
              listMethods={listMethods}
              listId={listId}
              belongsToCurrentUser={belongsToCurrentUser}
              updatePage={updatePage}
            />
          </ErrorBoundary>
        </SearchInsightsProvider>
      </GeoLocationProvider>
    </Page>
  );
};

export default SearchApp;
